import {
  ArrowLeft,
  CaretDown,
  ChatCircle,
  DotsThree,
  NotePencil,
  Phone,
  Plus,
} from "@phosphor-icons/react";
import { useQuery } from "convex/react";
import { useMemo, useState } from "react";

import { api } from "../../convex/_generated/api";
import type { BusinessMembership, ConversationSummary } from "../inbox/types";
import { WorkspaceLink } from "../workspace/WorkspaceLink";

function formatDate(value: number | undefined) {
  if (!value) return "Not available";
  return new Intl.DateTimeFormat("en-ZA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
}

function formatCustomerSince(conversation: ConversationSummary | undefined) {
  if (!conversation) return "Not available";
  return new Intl.DateTimeFormat("en-ZA", { dateStyle: "medium" }).format(
    conversation.createdAt,
  );
}

function readSessionTags(storageKey: string) {
  const savedTags = window.sessionStorage.getItem(storageKey);
  if (!savedTags) return [];
  try {
    const parsedTags: unknown = JSON.parse(savedTags);
    return Array.isArray(parsedTags) &&
      parsedTags.every((tag) => typeof tag === "string")
      ? parsedTags
      : [];
  } catch {
    window.sessionStorage.removeItem(storageKey);
    return [];
  }
}

// The profile intentionally keeps the customer record, activity, timeline, and tag editor together.
// eslint-disable-next-line complexity
export function CustomerProfilePage({
  business,
  customerId,
}: {
  business: BusinessMembership;
  customerId: string;
}) {
  const conversationsQuery = useQuery(api.conversations.listForBusiness, {
    businessId: business.businessId,
  }) as ConversationSummary[] | undefined;
  const conversation = useMemo(
    () => conversationsQuery?.find((item) => item.contactId === customerId),
    [conversationsQuery, customerId],
  );
  const [isTagEditorOpen, setIsTagEditorOpen] = useState(false);
  const [tagDraft, setTagDraft] = useState("");
  const tagStorageKey = `nkuli:customer-tags:${customerId}`;
  const [tags, setTags] = useState<string[]>(() =>
    readSessionTags(tagStorageKey),
  );

  const saveTags = (nextTags: string[]) => {
    setTags(nextTags);
    window.sessionStorage.setItem(tagStorageKey, JSON.stringify(nextTags));
  };

  const addTag = () => {
    const nextTag = tagDraft.trim().replace(/,$/, "").trim();
    if (!nextTag || tags.includes(nextTag)) return;
    saveTags([...tags, nextTag]);
    setTagDraft("");
  };

  const name = conversation?.contact?.displayName ?? "Ntsikelelo Khuzwayo";
  const identity =
    conversation?.contact?.normalizedPhoneIdentity ??
    conversation?.contact?.canonicalWhatsAppIdentity ??
    "Not available";
  const messageCount = conversation?.messageCount ?? 0;
  const lastMessage = conversation?.lastMessage?.text;

  return (
    <main aria-label={`${name} customer profile`} className="customer-profile">
      <header className="customer-profile-header">
        <div className="customer-profile-heading">
          <WorkspaceLink
            ariaLabel="Back to customers"
            className="customer-profile-back"
            href="/app/customers"
          >
            <ArrowLeft aria-hidden="true" size={18} />
          </WorkspaceLink>
          <span className="customer-profile-breadcrumb">Customers</span>
          <span aria-hidden="true" className="customer-profile-slash">
            /
          </span>
          <h1>{name}</h1>
        </div>
        <div className="customer-profile-actions">
          <button className="customer-profile-action" type="button">
            More actions <CaretDown aria-hidden="true" size={15} />
          </button>
          <button
            aria-label="Previous customer"
            className="customer-profile-icon-button"
            type="button"
          >
            <CaretDown aria-hidden="true" size={16} weight="bold" />
          </button>
          <button
            aria-label="Next customer"
            className="customer-profile-icon-button"
            type="button"
          >
            <CaretDown aria-hidden="true" size={16} weight="bold" />
          </button>
        </div>
      </header>

      <div className="customer-profile-layout">
        <section className="customer-profile-main">
          <div className="customer-profile-summary">
            <div>
              <span>Amount spent</span>
              <strong>R 0.00</strong>
            </div>
            <div>
              <span>Orders</span>
              <strong>0</strong>
            </div>
            <div>
              <span>Customer since</span>
              <strong>{formatCustomerSince(conversation)}</strong>
            </div>
            <div>
              <span>RFM</span>
              <strong>{conversation ? "Prospect" : "Not available"}</strong>
            </div>
          </div>

          <section className="customer-profile-section">
            <div className="customer-profile-section-heading">
              <h2>Activity</h2>
              <button aria-label="Activity actions" type="button">
                <DotsThree aria-hidden="true" size={20} weight="bold" />
              </button>
            </div>
            <div className="customer-profile-empty-state">
              <ChatCircle aria-hidden="true" size={28} />
              <div>
                <h3>
                  {conversation ? "Conversation activity" : "No activity yet"}
                </h3>
                <p>
                  {conversation
                    ? `${messageCount} ${messageCount === 1 ? "message" : "messages"} recorded in Inbox.`
                    : "This customer has not been linked to an Inbox conversation yet."}
                </p>
              </div>
              <WorkspaceLink
                className="customer-profile-secondary-button"
                href="/app/inbox"
              >
                Open Inbox
              </WorkspaceLink>
            </div>
          </section>

          <section className="customer-profile-section">
            <h2>Timeline</h2>
            <div className="customer-profile-comment-box">
              <div className="customer-profile-avatar">
                {name.slice(0, 2).toUpperCase()}
              </div>
              <span>Leave a comment...</span>
              <button aria-label="Edit timeline comment" type="button">
                <NotePencil aria-hidden="true" size={18} />
              </button>
            </div>
            <div className="customer-profile-timeline">
              <div className="customer-profile-timeline-line" />
              <div className="customer-profile-timeline-event">
                <span className="customer-profile-timeline-dot" />
                <div>
                  <span className="customer-profile-timeline-date">
                    {formatDate(conversation?.createdAt)}
                  </span>
                  <strong>Customer record linked</strong>
                  <p>
                    {conversation
                      ? `${messageCount} messages are associated with this customer.`
                      : "This customer profile is ready for activity."}
                  </p>
                </div>
              </div>
              {lastMessage ? (
                <div className="customer-profile-timeline-event">
                  <span className="customer-profile-timeline-dot" />
                  <div>
                    <span className="customer-profile-timeline-date">
                      Last message · {formatDate(conversation?.lastActivityAt)}
                    </span>
                    <strong>
                      {conversation?.lastMessage?.direction === "INBOUND"
                        ? "Customer said"
                        : "Nkuli replied"}
                    </strong>
                    <p>{lastMessage}</p>
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        </section>

        <aside className="customer-profile-sidebar">
          <section className="customer-profile-sidebar-section">
            <div className="customer-profile-section-heading">
              <h2>Contact information</h2>
              <button aria-label="Contact information actions" type="button">
                <DotsThree aria-hidden="true" size={20} weight="bold" />
              </button>
            </div>
            {identity === "Not available" ? (
              <p className="customer-profile-muted">Phone not available</p>
            ) : (
              <a
                className="customer-profile-contact-link"
                href={`tel:${identity}`}
              >
                <Phone aria-hidden="true" size={17} />
                {identity}
              </a>
            )}
            <p className="customer-profile-muted">Email not available</p>
            <p className="customer-profile-muted">Language not available</p>
          </section>

          <section className="customer-profile-sidebar-section">
            <h2>Default address</h2>
            <p className="customer-profile-muted">No address has been added.</p>
          </section>

          <section className="customer-profile-sidebar-section">
            <h2>Marketing subscriptions</h2>
            <p className="customer-profile-muted">No subscriptions recorded.</p>
          </section>

          <section className="customer-profile-sidebar-section">
            <h2>Tags</h2>
            {tags.length ? (
              <div className="customer-profile-tag-list">
                {tags.map((tag) => (
                  <button
                    aria-label={`Remove ${tag} tag`}
                    className="customer-profile-tag"
                    key={tag}
                    onClick={() =>
                      saveTags(tags.filter((currentTag) => currentTag !== tag))
                    }
                    type="button"
                  >
                    {tag} <span aria-hidden="true">×</span>
                  </button>
                ))}
              </div>
            ) : null}
            {isTagEditorOpen ? (
              <div className="customer-profile-tag-editor">
                <input
                  autoFocus
                  aria-label="New customer tag"
                  onChange={(event) => setTagDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addTag();
                    }
                    if (event.key === "Escape") setIsTagEditorOpen(false);
                  }}
                  placeholder="Type a tag"
                  value={tagDraft}
                />
                <button onClick={addTag} type="button">
                  Add
                </button>
              </div>
            ) : (
              <button
                className="customer-profile-tags-button"
                onClick={() => setIsTagEditorOpen(true)}
                type="button"
              >
                <Plus aria-hidden="true" size={16} /> Add tags
              </button>
            )}
          </section>
        </aside>
      </div>
    </main>
  );
}
